import { memo, ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    ClickAwayListener,
    styled,
    SxProps,
    Theme,
    tooltipClasses,
    TooltipProps,
    Typography,
} from '@mui/material';
import { StyledTooltip } from '@tymely/components';

import NavItemButton from './NavItemButton';
import NavItem from './NavItem';

const MenuItem = styled(NavItem)(
    ({ theme }) => `
    border-top-left-radius: ${theme.spacing(4)};
    border-bottom-left-radius: ${theme.spacing(4)};
    margin-left: ${theme.spacing(1)};
    border-width: 0;
`,
);

const WarningTooltip = styled((props: Omit<TooltipProps, 'title'> & { warning?: ReactNode }) => (
    <StyledTooltip
        {...props}
        placement="top"
        title={
            props.warning && (
                <Typography fontSize="fontTiny" variant="subtitle2">
                    {props.warning}
                </Typography>
            )
        }
    ></StyledTooltip>
))`
    .${tooltipClasses.tooltip} {
        background-color: ${({ theme }) => theme.palette.error.main};
        height: auto;
    }
    .${tooltipClasses.arrow} {
        color: ${({ theme }) => theme.palette.error.main};
    }
`;

const SideBarItem = memo(
    (props: {
        active?: boolean;
        icon: ReactElement;
        title?: string;
        children?: ReactElement | ((anchor: HTMLElement | null, active: boolean, setClose: () => void) => ReactElement);
        sx?: SxProps<Theme>;
        hasMenu?: boolean;
        warning?: ReactNode;
        disabled?: boolean;
        onActive?: (active: boolean) => void;
        onClick?: () => void;
    }) => {
        const [active, setActive] = useState(Boolean(props.active));
        const anchorRef = useRef<HTMLLIElement>(null);
        const ItemComp = props.hasMenu ? MenuItem : NavItem;
        const activable = !props.disabled && (props.hasMenu || props.onActive);

        const onNavToggle = useCallback(() => {
            if (activable) {
                setActive(!active);
                props.onActive?.(!active);
            }
            props.onClick?.();
        }, [active, activable, anchorRef.current, props.onClick, props.onActive]);

        useEffect(() => {
            setActive(Boolean(props.active));
        }, [props.active]);

        const children =
            typeof props.children === 'function'
                ? props.children?.(anchorRef.current, Boolean(active), onNavToggle)
                : props.children;

        return (
            <ClickAwayListener onClickAway={() => active && setActive(false)}>
                <Box>
                    <StyledTooltip
                        disableHoverListener={!props.title || props.disabled}
                        title={active ? null : <Typography variant="subtitle2">{props.title}</Typography>}
                        placement="right"
                    >
                        <ItemComp
                            ref={anchorRef}
                            active={active}
                            sx={props.sx}
                            onMouseDown={(e) => {
                                e.preventDefault?.();
                            }}
                            onClick={onNavToggle}
                        >
                            <WarningTooltip open={Boolean(props.warning)} warning={props.warning}>
                                <NavItemButton disableRipple disabled={props.disabled}>
                                    {props.icon}
                                </NavItemButton>
                            </WarningTooltip>
                        </ItemComp>
                    </StyledTooltip>
                    {children}
                </Box>
            </ClickAwayListener>
        );
    },
);

SideBarItem.displayName = 'BarItem';
export default SideBarItem;
